import { z } from "zod";

import {
  dateString,
  endDateAfterStart,
  optionalText,
  requiredText
} from "@/lib/forms/schema";

import type {
  EducationPayload,
  ForeignNationalEducationHistoryEntry
} from "../../../../api/types/foreignNationalRecords";
import {
  dateInputToIso,
  normalizeText,
  toDateInputValue
} from "../../lib/format";
import { byCurrentThenStartDesc } from "../../lib/records";

export type EducationDraft = {
  institution: string;
  city: string;
  country: string;
  degree: string;
  fieldOfStudy: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  notes: string;
};

export const DEGREE_OPTIONS = [
  "High school diploma",
  "Associate",
  "Bachelor's",
  "Master's",
  "MBA",
  "J.D.",
  "M.D.",
  "Ph.D.",
  "Professional certificate",
  "Other"
] as const;

export const emptyEducationDraft = (): EducationDraft => ({
  institution: "",
  city: "",
  country: "",
  degree: "",
  fieldOfStudy: "",
  startDate: "",
  endDate: "",
  isCurrent: false,
  notes: ""
});

export const educationToDraft = (
  e: ForeignNationalEducationHistoryEntry
): EducationDraft => ({
  institution: normalizeText(e.institution),
  city: normalizeText(e.city),
  country: normalizeText(e.country),
  degree: normalizeText(e.degree),
  fieldOfStudy: normalizeText(e.field_of_study),
  startDate: toDateInputValue(e.start_date),
  endDate: toDateInputValue(e.end_date),
  isCurrent: Boolean(e.is_current),
  notes: normalizeText(e.notes)
});

export const educationDraftSchema: z.ZodType<EducationDraft, EducationDraft> =
  endDateAfterStart(
    z.object({
      institution: requiredText("School"),
      city: optionalText(),
      country: optionalText(),
      degree: optionalText(),
      fieldOfStudy: optionalText(),
      startDate: dateString(),
      endDate: dateString(),
      isCurrent: z.boolean(),
      notes: optionalText()
    }),
    "startDate",
    "endDate",
    "Graduation can't be before the start date."
  );

export const educationDraftToPayload = (
  d: EducationDraft
): EducationPayload => ({
  institution: normalizeText(d.institution) || null,
  city: normalizeText(d.city) || null,
  country: normalizeText(d.country) || null,
  degree: normalizeText(d.degree) || null,
  field_of_study: normalizeText(d.fieldOfStudy) || null,
  start_date: dateInputToIso(d.startDate),
  end_date: d.isCurrent ? null : dateInputToIso(d.endDate),
  is_current: d.isCurrent,
  notes: normalizeText(d.notes) || null
});

export const sortEducation =
  byCurrentThenStartDesc<ForeignNationalEducationHistoryEntry>(
    (e) => e.start_date ?? e.end_date,
    (e) => Boolean(e.is_current)
  );

export const educationFocusDomId = (id: string): string => `education-${id}`;
